import { useEffect, useRef, useState } from 'react';
import styles from './Toast.module.css';

export type ToastSeverity = 'success' | 'error' | 'info';

export interface ToastProps {
  open: boolean;
  message: string;
  severity?: ToastSeverity;
  /** Auto-dismiss after this many ms; pass `null` to require an explicit close. MUI Snackbar-style. */
  autoHideDuration?: number | null;
  onClose?: () => void;
}

// The dot the DS Sonner draws (Figma 14060:191440) is the primary ink; severity only ever recolours it.
const ACCENT: Record<ToastSeverity, string> = {
  success: 'var(--ds-green-success-600)',
  error: 'var(--ds-fill-destructive-hover)',
  info: 'var(--ds-text-primary)',
};

// A single overlay toast (MUI Snackbar-flavoured: `open`/`message`/`onClose`/`autoHideDuration`).
// Fixed to the viewport bottom so it never shifts page content; fades + slides in on open and back
// out before unmounting. Content is snapshotted on open so it stays put through the exit animation
// even if the caller clears `message` the same tick it flips `open` to false.
export function Toast({ open, message, severity = 'info', autoHideDuration = 4000, onClose }: ToastProps) {
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);
  const [content, setContent] = useState({ message, severity });
  const exitTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (open) {
      if (exitTimer.current) clearTimeout(exitTimer.current);
      setContent({ message, severity });
      setMounted(true);
      const r = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(r);
    }
    setVisible(false);
    // Fallback in case the exit transitionend doesn't fire (e.g. closed before it finished entering).
    exitTimer.current = setTimeout(() => setMounted(false), 320);
    return () => {
      if (exitTimer.current) clearTimeout(exitTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open || autoHideDuration == null) return;
    const id = setTimeout(() => onClose?.(), autoHideDuration);
    return () => clearTimeout(id);
  }, [open, autoHideDuration, onClose]);

  if (!mounted) return null;

  return (
    <div className={styles.wrap}>
      <div
        role={content.severity === 'error' ? 'alert' : 'status'}
        aria-live={content.severity === 'error' ? 'assertive' : 'polite'}
        onClick={onClose}
        onTransitionEnd={() => {
          if (!open) setMounted(false);
        }}
        className={`${styles.toast} ${visible ? styles.visible : ''}`}
      >
        <span aria-hidden className={styles.dot} style={{ background: ACCENT[content.severity] }} />
        <span>{content.message}</span>
      </div>
    </div>
  );
}
